import { useEffect, useRef, useState } from "react";
import {
  ArrowUpIcon,
  ClipboardListIcon,
  ClockIcon,
  RotateCcwIcon,
  SmartphoneIcon,
} from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { SadieSimulatorEnvelopeChips } from "@/components/tools/sadie-simulator/SadieSimulatorEnvelopeChips";
import { SadieSimulatorSearchNote } from "@/components/tools/sadie-simulator/SadieSimulatorSearchNote";
import { SadieSimulatorTypingDots } from "@/components/tools/sadie-simulator/SadieSimulatorTypingDots";
import { Button } from "@ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";
import { Textarea } from "@ui/textarea";

import { usePieceReveal } from "@/hooks/usePieceReveal";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { sadieSimulator } from "@/lib/sadieSimulator";
import { cn } from "@/lib/utils";
import { SadieSimulatorEntry } from "@/types";

const MOBILE_SIMULATOR_PATH = "/mobile/sadie-simulator";

export const SadieSimulatorConversation = ({
  agentName,
  entries,
  isEnded,
  isFollowUpAvailable,
  isHandoffAvailable,
  isHandoffPending,
  isLoopedToSession1,
  isPending,
  isSetupLoading,
  onContinueToPulse,
  onFollowUp,
  onReset,
  onSend,
  onStart,
  priorEntries,
  workerTextsFirst,
}: {
  agentName: string;
  entries: SadieSimulatorEntry[];
  isEnded: boolean;
  // A pulse opener is waiting on a reply and the one follow-up has not gone
  // out yet, the same conditions production sends it under.
  isFollowUpAvailable: boolean;
  // Session 1 has resolved and can be handed into Session 2.
  isHandoffAvailable: boolean;
  isHandoffPending: boolean;
  // Session 3 set loopedToSession, so production hands the thread to
  // Session 1 here and the simulator stops.
  isLoopedToSession1: boolean;
  isPending: boolean;
  // The prompt is being re-rendered after a setup change. Kept apart from
  // isPending so the typing dots do not show for it.
  isSetupLoading: boolean;
  onContinueToPulse: () => void;
  onFollowUp: () => void;
  onReset: () => void;
  onSend: (text: string) => void;
  onStart: () => void;
  // The Session 1 thread a running Session 2 was handed off from. Shown
  // dimmed above the live thread, never sent to the agent.
  priorEntries: SadieSimulatorEntry[];
  // Session 3: there is no opener, the worker's text starts the thread.
  workerTextsFirst: boolean;
}) => {
  /* React Hooks */
  const [draft, setDraft] = useState("");
  const successToast = useSuccessToast();
  const bottomRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [entries.length, isPending, priorEntries.length]);

  /* Helpers */
  const isStarted = entries.length > 0;
  // Typing stays open while a turn is in flight, the way a chat does. Only
  // sending waits. Disabling the box would also drop focus on every send.
  const isInputDisabled = (!isStarted && !workerTextsFirst) || isEnded;
  const isSendDisabled = isInputDisabled || isPending || isSetupLoading;

  // A reset empties the transcript. A half-typed reply should not outlive it.
  // Once the opener lands, the box is ready to type into.
  useEffect(() => {
    if (!isStarted) {
      setDraft("");
      return;
    }
    draftRef.current?.focus();
  }, [isStarted]);

  const inputPlaceholder = () => {
    if (isEnded) return "The conversation ended. Reset to start another.";
    if (isStarted) return "Reply as the worker";
    if (workerTextsFirst) return "Text in as the worker";
    return "Send the opener first";
  };

  /* Handlers */
  const handleCopy = () => {
    void navigator.clipboard.writeText(
      sadieSimulator.formatTranscript(entries, agentName),
    );
    successToast({ description: "Transcript copied" });
  };

  const handleSubmit = () => {
    const text = draft.trim();
    if (!text || isSendDisabled) return;
    onSend(text);
    setDraft("");
  };

  /* Render */
  return (
    <Column className="h-full min-h-0 bg-white">
      <Row className="items-center justify-between px-4 py-2">
        <Text className="text-sm font-medium text-slate-700">Conversation</Text>
        <Row className="items-center gap-0.5">
          <SimulatorIconButton
            disabled={false}
            label="Open on a phone"
            onClick={() => {
              window.open(MOBILE_SIMULATOR_PATH, "_blank", "noopener");
            }}
          >
            <SmartphoneIcon className="h-3.5 w-3.5" />
          </SimulatorIconButton>
          <SimulatorIconButton
            disabled={!isStarted}
            label="Copy transcript"
            onClick={handleCopy}
          >
            <ClipboardListIcon className="h-3.5 w-3.5" />
          </SimulatorIconButton>
          <SimulatorIconButton
            disabled={isPending || !isStarted}
            label="Reset conversation"
            onClick={onReset}
          >
            <RotateCcwIcon className="h-3.5 w-3.5" />
          </SimulatorIconButton>
        </Row>
      </Row>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Column className="mx-auto min-h-full w-full max-w-2xl gap-6 px-6 py-8">
          {priorEntries.length > 0 ? (
            <Column className="gap-6">
              <Column className="gap-6 opacity-50">
                {priorEntries.map((entry, i) => (
                  <SimulatorMessage animate={false} entry={entry} key={i} />
                ))}
              </Column>
              <Row className="items-center gap-3">
                <div className="h-px flex-1 bg-slate-200" />
                <Text className="text-center text-xs font-normal text-slate-400">
                  Session 1 ended. Sadie carries a digest of it forward, not the
                  messages.
                </Text>
                <div className="h-px flex-1 bg-slate-200" />
              </Row>
            </Column>
          ) : null}
          {/* After a handoff this sits under the Session 1 thread, so the
              next step is where the eye already is. */}
          {!isStarted && !isPending ? (
            <Column className="m-auto items-center gap-4 text-center">
              {workerTextsFirst ? (
                <Text className="text-sm text-slate-500">
                  The worker texts first. Type their message below.
                </Text>
              ) : (
                <>
                  <Text className="text-sm text-slate-500">
                    {agentName} sends the first message. Then you reply as the
                    worker.
                  </Text>
                  <Button disabled={isSetupLoading} onClick={onStart}>
                    Send the opener
                  </Button>
                </>
              )}
            </Column>
          ) : null}
          {entries.map((entry, i) => (
            <SimulatorMessage
              animate={i === entries.length - 1}
              entry={entry}
              key={i}
            />
          ))}
          {isPending ? <SadieSimulatorTypingDots /> : null}
          <div ref={bottomRef} />
        </Column>
      </div>

      <Column className="mx-auto w-full max-w-2xl gap-2 px-6 pb-4">
        {isLoopedToSession1 ? (
          <Text className="pb-1 text-center text-xs font-normal text-slate-400">
            Production hands this conversation to Session 1 here. The simulator
            stops.
          </Text>
        ) : null}
        {isHandoffAvailable ? (
          <Column className="items-center gap-1.5 pb-1">
            <Button
              disabled={isHandoffPending || isSetupLoading}
              onClick={onContinueToPulse}
              size="sm"
            >
              {isHandoffPending
                ? "Writing the digest"
                : "Continue into Session 2"}
            </Button>
            <Text className="text-xs font-normal text-slate-400">
              The digest agent reads this conversation. Session 2 opens on what
              it kept.
            </Text>
          </Column>
        ) : null}
        {isFollowUpAvailable ? (
          <Row className="justify-center">
            <Button
              className="h-7 gap-1.5 rounded-full border border-dashed border-slate-300 bg-white px-3 text-xs font-medium text-slate-500 shadow-none hover:border-slate-400 hover:bg-slate-50 hover:text-slate-700"
              disabled={isPending || isSetupLoading}
              onClick={onFollowUp}
              size="sm"
              variant="ghost"
            >
              <ClockIcon className="h-3.5 w-3.5" />A day passes with no reply.
              Send the follow-up
            </Button>
          </Row>
        ) : null}
        <Column className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-colors focus-within:border-slate-300">
          <Textarea
            autoExpand
            className="max-h-40 min-h-0 resize-none rounded-none border-0 bg-transparent px-4 pb-1 pt-3.5 text-[15px] leading-relaxed shadow-none placeholder:text-slate-400 md:text-[15px]"
            disabled={isInputDisabled}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={inputPlaceholder()}
            ref={draftRef}
            rows={1}
            value={draft}
          />
          <Row className="items-center justify-between px-3 pb-2.5">
            <Text className="pl-1 text-xs text-slate-400">
              Enter to send. Shift + Enter for a new line.
            </Text>
            <Button
              aria-label="Send"
              className="h-8 w-8 rounded-full"
              disabled={isSendDisabled || draft.trim().length === 0}
              onClick={handleSubmit}
              size="icon"
            >
              <ArrowUpIcon className="h-4 w-4" />
            </Button>
          </Row>
        </Column>
        <Text className="text-center text-xs text-slate-400">
          Simulated. Nothing is sent or saved.
        </Text>
      </Column>
    </Column>
  );
};

const SimulatorIconButton = ({
  children,
  disabled,
  label,
  onClick,
}: {
  children: React.ReactNode;
  disabled: boolean;
  label: string;
  onClick: () => void;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        aria-label={label}
        className="h-7 w-7 text-slate-500"
        disabled={disabled}
        onClick={onClick}
        size="icon"
        variant="ghost"
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

const SimulatorMessage = ({
  animate,
  entry,
}: {
  animate: boolean;
  entry: SadieSimulatorEntry;
}) => {
  const isAgent = entry.role === "assistant";
  const pieces = isAgent
    ? sadieSimulator.splitPieces(entry.content)
    : [entry.content];
  const isSilent = isAgent && pieces.length === 0;
  const { revealed, revealAll } = usePieceReveal(
    pieces.length,
    animate && isAgent,
  );
  const lastPieceRef = useRef<HTMLDivElement>(null);
  const isComplete = revealed >= pieces.length;

  useEffect(() => {
    lastPieceRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [revealed]);

  // The agent reads as plain text on the page. The worker reads as a sent
  // bubble on the right, the way a chat app draws the person typing.
  const pieceClassName = isAgent
    ? "whitespace-pre-wrap text-[15px] leading-relaxed text-slate-900"
    : "self-end whitespace-pre-wrap rounded-2xl bg-slate-100 px-4 py-2.5 text-[15px] leading-relaxed text-slate-900";

  return (
    <Column
      className={cn("gap-3", isAgent ? "w-full" : "max-w-[80%] self-end")}
      onClick={revealAll}
    >
      {entry.search ? <SadieSimulatorSearchNote search={entry.search} /> : null}
      {isSilent ? (
        <div className="text-[15px] italic text-slate-400">
          {"(nothing sent)"}
        </div>
      ) : null}
      {pieces.slice(0, revealed).map((piece, i) => (
        <div
          className={pieceClassName}
          key={i}
          ref={i === revealed - 1 ? lastPieceRef : undefined}
        >
          {piece}
        </div>
      ))}
      {!isComplete ? <SadieSimulatorTypingDots /> : null}
      {entry.turn && isComplete ? (
        <SadieSimulatorEnvelopeChips turn={entry.turn} />
      ) : null}
    </Column>
  );
};
